"use client";

import { forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import type { ImageType } from "@/lib/image-utils";

export const SHARE_CARD_WIDTH = 600;

interface ShareProfileCardProps {
  /** Display name shown on the card */
  name: string;
  /** Pre-formatted professional title, e.g. "Senior Corporate Lawyer" */
  title?: string;
  /** Pre-formatted location, e.g. "Ho Chi Minh City" */
  location?: string;
  /** URL the QR code encodes — must be the public production URL */
  url: string;
  /** Avatar as a data URL (pre-fetched to avoid canvas taint). Null = initials fallback. */
  avatarDataUrl?: string | null;
  /** "logo" shows the image contained on white with a gold ring; "photo" covers on navy. */
  imageType?: ImageType;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

/**
 * Branded shareable profile card. Rendered at fixed pixel dimensions
 * (600×900) so it can be rasterized for download via html-to-image at
 * any pixelRatio. The modal previews this same component scaled down.
 *
 * Note: this component intentionally avoids next/image — html-to-image
 * needs plain <img> tags with same-origin or data-URL sources.
 */
const ShareProfileCard = forwardRef<HTMLDivElement, ShareProfileCardProps>(
  function ShareProfileCard({ name, title, location, url, avatarDataUrl, imageType = "photo" }, ref) {
    const isLogo = imageType === "logo";
    const initials = getInitials(name);
    // Strip protocol + leading slash for display
    const displayUrl = url.replace(/^https?:\/\//, "").replace(/\/$/, "");

    // Adaptive name sizing — long names step down before truncating.
    // ⚠️ Calibrated against PLAYFAIR at 32px (~0.55em per char); content width
    // is ~490px. EB Garamond replaced it 2026-09-08 and runs narrower, so this
    // first guess now errs SMALL — harmless (the export measures the real text
    // and steps down from here), but re-measure if a long name ever wraps.
    // so ~28 chars fit at 32px and ~22 at 38px. Be conservative.
    const nameLength = name.length;
    const nameSize = nameLength <= 16 ? 36 : nameLength <= 22 ? 30 : 24;

    return (
      <div
        ref={ref}
        style={{
          width: SHARE_CARD_WIDTH,
          backgroundColor: "#ffffff",
          border: "1px solid rgba(4, 0, 68, 0.15)",
          borderRadius: 2,
          padding: "55px 55px 55px 55px",
          fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
          color: "#040044",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* Logo */}
        {/* Use plain <img> so html-to-image can serialize it. SVG from /public is same-origin. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/EasyTiger_FullLogo_Colour.svg"
          alt="Easytiger"
          width={180}
          height={36}
          style={{ display: "block", height: 36, width: "auto" }}
        />

        {/* Avatar */}
        <div
          style={{
            marginTop: 34,
            width: 144,
            height: 144,
            borderRadius: "50%",
            overflow: "hidden",
            border: isLogo ? "2px solid #FFB200" : "2px solid rgba(4, 0, 68, 0.1)",
            backgroundColor: isLogo ? "#ffffff" : "#040044",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {avatarDataUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={avatarDataUrl}
              alt=""
              width={144}
              height={144}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span
              style={{
                fontFamily: "'EB Garamond', ui-serif, Georgia, serif",
                fontSize: 48,
                color: "#FFB200",
                fontWeight: 500,
                lineHeight: 1,
              }}
            >
              {initials}
            </span>
          )}
        </div>

        {/* Name */}
        <h2
          style={{
            marginTop: 21,
            marginBottom: 0,
            fontFamily: "'EB Garamond', ui-serif, Georgia, serif",
            fontSize: nameSize,
            fontWeight: 500,
            lineHeight: 1.15,
            color: "#040044",
            textAlign: "center",
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            alignSelf: "stretch",
          }}
        >
          {name}
        </h2>

        {/* Title + location */}
        {title && (
          <p
            style={{
              marginTop: 13,
              marginBottom: 0,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#040044",
              textAlign: "center",
            }}
          >
            {title}
          </p>
        )}
        {location && (
          <p
            style={{
              marginTop: 5,
              marginBottom: 0,
              fontSize: 13,
              color: "#3D4451",
              textAlign: "center",
            }}
          >
            {location}
          </p>
        )}

        {/* Gold divider */}
        <div
          style={{
            marginTop: 21,
            width: 32,
            height: 1,
            backgroundColor: "#FFB200",
          }}
        />

        {/* QR */}
        <div
          style={{
            marginTop: 21,
            padding: 13,
            border: "1px solid rgba(4, 0, 68, 0.1)",
            borderRadius: 2,
            backgroundColor: "#ffffff",
          }}
        >
          <QRCodeCanvas
            value={url}
            size={260}
            bgColor="#ffffff"
            fgColor="#040044"
            level="H"
            imageSettings={{
              src: "/EasyTiger_BadgeOnly_Colour.svg",
              height: 52,
              width: 52,
              excavate: true,
            }}
          />
        </div>

        {/* Caption + URL */}
        <p
          style={{
            marginTop: 21,
            marginBottom: 0,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#040044",
          }}
        >
          Scan to view profile
        </p>
        <p
          style={{
            marginTop: 5,
            marginBottom: 0,
            fontSize: 13,
            color: "#545A64",
            textAlign: "center",
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            alignSelf: "stretch",
          }}
        >
          {displayUrl}
        </p>

      </div>
    );
  },
);

export default ShareProfileCard;
